// ClientDetails.jsx - Client Details & Management
// v3.97 - Admin interface for viewing/managing individual clients

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, Edit, DollarSign, Server, Bot, Calendar, Clock,
  Mail, Phone, Building2, User, Users, CreditCard, Plus, Trash2,
  RefreshCw, CheckCircle, AlertCircle, X, ChevronRight,
  AlertTriangle, History, FileText, TrendingUp, TrendingDown,
  Receipt, Download, ChevronDown, ChevronUp, ShieldCheck, ShieldX,
  GitBranch, Minus, MessageCircle, ShieldAlert, ExternalLink,
  Target, BarChart3, Eye
} from 'lucide-react'
import { clientAdminApi } from '../services/api'
import InvoiceEditor from '../components/InvoiceEditor'
import './ClientDetails.css'

function ClientDetails() {
  const { id } = useParams()
  const navigate = useNavigate()

  // Data states
  const [client, setClient] = useState(null)
  const [dialers, setDialers] = useState([])
  const [availableDialers, setAvailableDialers] = useState([])
  const [transactions, setTransactions] = useState([])
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  // Auto-computed next invoice period (from cron's Phase 2 logic)
  const [nextCycle, setNextCycle] = useState(null)

  // Active tab
  const [activeTab, setActiveTab] = useState('overview')

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('edit') // edit, credit, setBalance, extend, setExtension, assign, markPaid, generateInvoice, editInvoice, addLineItem

  // Invoice states
  const [expandedInvoice, setExpandedInvoice] = useState(null)
  const [currentInvoice, setCurrentInvoice] = useState(null)
  const [paidAmount, setPaidAmount] = useState('')
  const [paidReference, setPaidReference] = useState('')
  const [editInvoiceData, setEditInvoiceData] = useState({})
  const [lineItemData, setLineItemData] = useState({})
  const [downloading, setDownloading] = useState(null)

  // Form states
  const [formData, setFormData] = useState({})
  const [creditAmount, setCreditAmount] = useState('')
  const [creditDescription, setCreditDescription] = useState('')
  const [extensionDays, setExtensionDays] = useState('')
  const [extensionReason, setExtensionReason] = useState('')
  const [setBalanceAmount, setSetBalanceAmount] = useState('')
  const [setBalanceReason, setSetBalanceReason] = useState('')
  const [setExtensionDaysValue, setSetExtensionDaysValue] = useState('')
  const [editExtensionReason, setEditExtensionReason] = useState('')
  const [selectedDialer, setSelectedDialer] = useState('')
  const [customBotPrice, setCustomBotPrice] = useState('')
  const [campaignType, setCampaignType] = useState('')

  // Toast state
  const [toast, setToast] = useState(null)

  // Invoice Editor state
  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState(null)

  // Invoice generation preview state
  const [generatePreview, setGeneratePreview] = useState(null)
  const [generatePeriodStart, setGeneratePeriodStart] = useState('')
  const [generatePeriodEnd, setGeneratePeriodEnd] = useState('')
  const [generateExchangeRate, setGenerateExchangeRate] = useState(280)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [generating, setGenerating] = useState(false)

  // Bot Changes state
  const [botChanges, setBotChanges] = useState([])
  const [botChangesStats, setBotChangesStats] = useState(null)
  const [clientAvatarsList, setClientAvatarsList] = useState([])
  const [allAvatars, setAllAvatars] = useState([])
  const [selectedAvatarToAdd, setSelectedAvatarToAdd] = useState('')
  const [avatarLabel, setAvatarLabel] = useState('')
  const [avatarType, setAvatarType] = useState('custom')
  const [botChangeFilterDialer, setBotChangeFilterDialer] = useState('all')
  const [botChangeType, setBotChangeType] = useState('addition')
  const [botChangeForm, setBotChangeForm] = useState({
    dialer_id: '', bot_count: '', effective_date: new Date().toISOString().split('T')[0], notes: ''
  })
  const [editingBotChange, setEditingBotChange] = useState(null)

  useEffect(() => {
    loadData()
  }, [id])

  const loadClientAvatars = async () => {
    try {
      const [avatarsRes, allRes] = await Promise.all([
        clientAdminApi.getClientAvatars(id),
        fetch(`${import.meta.env.VITE_API_URL || ''}/api/tables/avatars`).then(r => r.json()).catch(() => [])
      ])
      if (avatarsRes.data?.success) setClientAvatarsList(avatarsRes.data.avatars || [])
      if (Array.isArray(allRes)) setAllAvatars(allRes)
    } catch (e) { console.error('Failed to load avatars:', e) }
  }

  const handleAssignAvatar = async () => {
    if (!selectedAvatarToAdd) return
    try {
      const res = await clientAdminApi.assignClientAvatar(id, {
        avatar_id: parseInt(selectedAvatarToAdd),
        label: avatarLabel || null,
        avatar_type: avatarType
      })
      if (res.data?.success) {
        showToast('Avatar assigned')
        setSelectedAvatarToAdd('')
        setAvatarLabel('')
        setAvatarType('custom')
        loadClientAvatars()
      }
    } catch (e) { showToast('Failed to assign', 'error') }
  }

  const handleRemoveAvatar = async (avatarId) => {
    if (!window.confirm('Remove this avatar from client?')) return
    try {
      const res = await clientAdminApi.removeClientAvatar(id, avatarId)
      if (res.data?.success) { showToast('Avatar removed'); loadClientAvatars() }
    } catch (e) { showToast('Failed', 'error') }
  }

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    try {
      const [clientRes, dialersRes, availableRes, historyRes, invoicesRes, botChangesRes, botStatsRes] = await Promise.all([
        clientAdminApi.getClient(id),
        clientAdminApi.getClientDialers(id),
        clientAdminApi.getAvailableDialers(),
        clientAdminApi.getBillingHistory(id, 100),
        clientAdminApi.getInvoices({ client_id: id }),
        clientAdminApi.getBotChanges(id),
        clientAdminApi.getBotChangesStats(id)
      ])

      if (clientRes.data?.success) setClient(clientRes.data.client)
      if (dialersRes.data?.success) setDialers(dialersRes.data.dialers || [])
      // Fetch the cron's computed next cycle for this client (fire-and-forget)
      fetch(`/api/client-admin/invoices/next-cycle/${id}`)
        .then(r => r.json()).then(d => setNextCycle(d)).catch(() => { /* ignore */ })
      if (availableRes.data?.success) setAvailableDialers(availableRes.data.dialers || [])
      if (historyRes.data?.success) setTransactions(historyRes.data.transactions || [])
      if (invoicesRes.data?.success) setInvoices(invoicesRes.data.invoices || [])
      if (botChangesRes.data?.success) setBotChanges(botChangesRes.data.changes || [])
      if (botStatsRes.data?.success) setBotChangesStats(botStatsRes.data)
    } catch (error) {
      console.error('Failed to load client data:', error)
      showToast('Failed to load client data', 'error')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  const openModal = (mode) => {
    setModalMode(mode)

    if (mode === 'edit') {
      setFormData({
        client_id: client?.client_id || '',
        name: client?.name || '',
        email: client?.email || '',
        phone: client?.phone || '',
        company: client?.company || '',
        status: client?.status || 'active',
        default_bot_price: client?.default_bot_price || 15.00,
        invoice_day: client?.invoice_day || 1,
        notes: client?.notes || '',
        password: '',
        contact_person: client?.contact_person || '',
        whatsapp_number: client?.whatsapp_number || '',
        follow_up_date: client?.follow_up_date?.split('T')[0] || '',
        risk_status: client?.risk_status || 'low',
        target_bots: client?.target_bots || '',
        payment_terms_days: client?.payment_terms_days || 7
      })
    } else if (mode === 'credit') {
      setCreditAmount('')
      setCreditDescription('')
    } else if (mode === 'setBalance') {
      setSetBalanceAmount(client?.balance || 0)
      setSetBalanceReason('')
    } else if (mode === 'extend') {
      setExtensionDays('')
      setExtensionReason('')
    } else if (mode === 'setExtension') {
      setSetExtensionDaysValue(client?.admin_extension_days || 0)
      setEditExtensionReason('')
    } else if (mode === 'assign') {
      setSelectedDialer('')
      setCustomBotPrice('')
      setCampaignType('')
    }

    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      if (modalMode === 'edit') {
        const updateData = { ...formData }
        if (!updateData.password) delete updateData.password
        // Ensure invoice_day is sent
        if (updateData.invoice_day) updateData.invoice_day = parseInt(updateData.invoice_day)

        const res = await clientAdminApi.updateClient(id, updateData)
        if (res.data?.success) {
          showToast('Client updated successfully')
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to update client', 'error')
        }
      } else if (modalMode === 'credit') {
        const res = await clientAdminApi.addCredit(id, parseFloat(creditAmount), creditDescription)
        if (res.data?.success) {
          showToast(`$${creditAmount} added successfully`)
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to add credit', 'error')
        }
      } else if (modalMode === 'setBalance') {
        const res = await clientAdminApi.setBalance(id, parseFloat(setBalanceAmount), setBalanceReason)
        if (res.data?.success) {
          showToast(`Balance set to $${parseFloat(setBalanceAmount).toFixed(2)}`)
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to set balance', 'error')
        }
      } else if (modalMode === 'extend') {
        const res = await clientAdminApi.extendClient(id, parseInt(extensionDays), extensionReason)
        if (res.data?.success) {
          showToast(`Extended by ${extensionDays} days`)
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to extend', 'error')
        }
      } else if (modalMode === 'setExtension') {
        const res = await clientAdminApi.setExtension(id, parseInt(setExtensionDaysValue), editExtensionReason)
        if (res.data?.success) {
          showToast(parseInt(setExtensionDaysValue) > 0 ? `Extension set to ${setExtensionDaysValue} days` : 'Extension cleared')
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to update extension', 'error')
        }
      } else if (modalMode === 'assign') {
        const res = await clientAdminApi.assignDialer(id, parseInt(selectedDialer), customBotPrice ? parseFloat(customBotPrice) : null, campaignType || null)
        if (res.data?.success) {
          showToast('Dialer assigned successfully')
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to assign dialer', 'error')
        }
      }
    } catch (error) {
      console.error('Form submission error:', error)
      showToast('An error occurred', 'error')
    }

    setShowModal(false)
  }

  const handleUpdateCampaignType = async (dialerId, newCampaignType) => {
    try {
      const res = await clientAdminApi.updateDialerAssignment(id, dialerId, { campaign_type: newCampaignType })
      if (res.data?.success) {
        setDialers(prev => prev.map(d => d.dialer_id === dialerId ? { ...d, campaign_type: newCampaignType || null } : d))
        showToast('Campaign type updated')
      }
    } catch (error) {
      showToast('Failed to update campaign type', 'error')
    }
  }

  const handleRemoveDialer = async (dialerId) => {
    if (!window.confirm('Remove this dialer from the client?')) return

    try {
      const res = await clientAdminApi.removeDialer(id, dialerId)
      if (res.data?.success) {
        showToast('Dialer removed')
        loadData()
      }
    } catch (error) {
      showToast('Failed to remove dialer', 'error')
    }
  }

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    })
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatDateTime = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'status-active'
      case 'suspended': return 'status-suspended'
      case 'pending': return 'status-pending'
      default: return ''
    }
  }

  const getBalanceClass = (balance) => {
    const bal = parseFloat(balance)
    if (bal <= 0) return 'balance-critical'
    if (bal <= 50) return 'balance-warning'
    return 'balance-good'
  }

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit': return <TrendingUp size={16} className="tx-icon credit" />
      case 'debit': return <TrendingDown size={16} className="tx-icon debit" />
      case 'refund': return <TrendingUp size={16} className="tx-icon refund" />
      case 'adjustment': return <FileText size={16} className="tx-icon adjustment" />
      default: return <FileText size={16} className="tx-icon" />
    }
  }

  // Invoice helper functions
  const getInvoiceStatusClass = (status, dueDate) => {
    if (status === 'pending' && new Date(dueDate) < new Date()) {
      return 'status-overdue'
    }
    switch (status) {
      case 'paid': return 'status-paid'
      case 'partial': return 'status-partial'
      case 'pending': return 'status-pending'
      case 'cancelled': return 'status-cancelled'
      default: return ''
    }
  }

  const formatInvoiceStatus = (status, dueDate) => {
    if (status === 'pending' && new Date(dueDate) < new Date()) {
      return 'Overdue'
    }
    return status?.charAt(0).toUpperCase() + status?.slice(1)
  }

  const formatPeriod = (start, end) => {
    const s = new Date(start)
    const e = new Date(end)
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  }

  const openInvoiceModal = (mode, invoice = null) => {
    setModalMode(mode)
    setCurrentInvoice(invoice)

    if (mode === 'markPaid' || mode === 'markPartial') {
      setPaidAmount(invoice?.subtotal_usd?.toString() || '')
      setPaidReference('')
    } else if (mode === 'generateInvoice') {
      // Calculate default period dates based on invoice_day
      const now = new Date()
      const invoiceDay = client?.invoice_day || 1
      const currentDay = now.getDate()
      let pStart, pEnd

      if (currentDay >= invoiceDay) {
        pStart = new Date(now.getFullYear(), now.getMonth(), invoiceDay)
        pEnd = new Date(now.getFullYear(), now.getMonth() + 1, invoiceDay - 1)
      } else {
        pStart = new Date(now.getFullYear(), now.getMonth() - 1, invoiceDay)
        pEnd = new Date(now.getFullYear(), now.getMonth(), invoiceDay - 1)
      }

      const pStartStr = pStart.toISOString().split('T')[0]
      const pEndStr = pEnd.toISOString().split('T')[0]
      setGeneratePeriodStart(pStartStr)
      setGeneratePeriodEnd(pEndStr)
      setGeneratePreview(null)
      setGenerateExchangeRate(280)

      // Load exchange rate
      clientAdminApi.getExchangeRate(false).then(res => {
        if (res.data?.success && res.data.rate) {
          setGenerateExchangeRate(res.data.rate)
        }
      })

      // Load preview if bot changes exist
      if (botChanges.length > 0) {
        setLoadingPreview(true)
        clientAdminApi.getBotTimeline(id, pStartStr, pEndStr).then(res => {
          if (res.data?.success) {
            setGeneratePreview(res.data.timeline)
          }
          setLoadingPreview(false)
        }).catch(() => setLoadingPreview(false))
      }
    } else if (mode === 'editInvoice' && invoice) {
      setEditInvoiceData({
        issue_date: invoice.issue_date?.split('T')[0] || '',
        due_date: invoice.due_date?.split('T')[0] || '',
        notes: invoice.notes || '',
        exchange_rate: invoice.exchange_rate || 280,
        line_items: [...(invoice.line_items || [])]
      })
      // Reset new line item form
      setLineItemData({
        description: '',
        campaign: '',
        qty: 1,
        rate: client?.default_bot_price || 15,
        days: 30,
        date_from: '',
        date_to: '',
        amount_usd: 0
      })
    } else if (mode === 'addLineItem') {
      setLineItemData({
        description: '',
        campaign: '',
        qty: 1,
        rate: client?.default_bot_price || 15,
        days: 30,
        date_from: '',
        date_to: '',
        amount_usd: 0
      })
    }

    setShowModal(true)
  }

  // Add line item to edit invoice modal (local state)
  const addLineItemToEdit = () => {
    if (!lineItemData.amount_usd && lineItemData.qty && lineItemData.rate && lineItemData.days) {
      lineItemData.amount_usd = (lineItemData.qty * lineItemData.rate * lineItemData.days) / 30
    }

    const newItem = {
      description: lineItemData.description || 'Custom Line Item',
      dialer_name: lineItemData.campaign || '-',
      bot_count: lineItemData.qty || 0,
      price_per_bot: lineItemData.rate || 0,
      days: lineItemData.days || 30,
      date_from: lineItemData.date_from,
      date_to: lineItemData.date_to,
      amount_usd: parseFloat(lineItemData.amount_usd) || 0
    }

    setEditInvoiceData(prev => ({
      ...prev,
      line_items: [...(prev.line_items || []), newItem]
    }))

    // Reset line item form
    setLineItemData({
      description: '',
      campaign: '',
      qty: 1,
      rate: client?.default_bot_price || 15,
      days: 30,
      date_from: '',
      date_to: '',
      amount_usd: 0
    })
  }

  // Add previous balance as line item
  const addPreviousBalanceRow = () => {
    const balanceItem = {
      description: 'Previous Balance Carried Forward',
      dialer_name: '-',
      bot_count: 0,
      price_per_bot: 0,
      days: 0,
      date_from: null,
      date_to: null,
      amount_usd: 0
    }

    setEditInvoiceData(prev => ({
      ...prev,
      line_items: [...(prev.line_items || []), balanceItem]
    }))
  }

  // Remove line item from edit modal (local state)
  const removeLineItemFromEdit = (index) => {
    setEditInvoiceData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }))
  }

  // Update line item amount in edit modal
  const updateLineItemAmount = (index, newAmount) => {
    setEditInvoiceData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) =>
        i === index ? { ...item, amount_usd: parseFloat(newAmount) || 0 } : item
      )
    }))
  }

  // Calculate total from line items
  const calculateEditTotal = () => {
    return (editInvoiceData.line_items || []).reduce((sum, item) => sum + (parseFloat(item.amount_usd) || 0), 0)
  }

  const handleMarkPaid = async (e) => {
    e.preventDefault()
    if (!currentInvoice) return

    try {
      const invoiceTotal = parseFloat(currentInvoice.subtotal_usd) || 0
      const paidAmountNum = parseFloat(paidAmount)
      // Use paidAmount if valid number, otherwise use invoice total
      const amount = !isNaN(paidAmountNum) ? paidAmountNum : invoiceTotal
      // Check if partial: amount must be greater than 0 and less than total
      const isPartial = amount > 0 && amount < invoiceTotal
      const res = await clientAdminApi.updateInvoiceStatus(currentInvoice.id, {
        status: isPartial ? 'partial' : 'paid',
        paid_amount: amount,
        paid_reference: paidReference
      })

      if (res.data?.success) {
        showToast(isPartial ? `Invoice marked as partial ($${amount.toFixed(2)} of $${invoiceTotal.toFixed(2)})` : 'Invoice marked as paid')
        loadData()
      } else {
        showToast(res.data?.error || 'Failed to update invoice', 'error')
      }
    } catch (error) {
      showToast('Failed to update invoice', 'error')
    }
    setShowModal(false)
  }

  // Get current user from localStorage for audit trail
  const getCurrentUser = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      return { id: user.id, username: user.username || user.name || 'Admin' }
    } catch {
      return { id: null, username: 'Admin' }
    }
  }

  const handleApproveInvoice = async (invoice) => {
    try {
      const user = getCurrentUser()
      const res = await clientAdminApi.approveInvoice(invoice.id, user.id, user.username)
      if (res.data?.success) {
        showToast('Invoice approved - now visible to client')
        loadData()
      } else {
        showToast(res.data?.error || 'Failed to approve invoice', 'error')
      }
    } catch (error) {
      showToast('Failed to approve invoice', 'error')
    }
  }

  const handleUnapproveInvoice = async (invoice) => {
    try {
      const res = await clientAdminApi.unapproveInvoice(invoice.id)
      if (res.data?.success) {
        showToast('Invoice unapproved - hidden from client')
        loadData()
      } else {
        showToast(res.data?.error || 'Failed to unapprove invoice', 'error')
      }
    } catch (error) {
      showToast('Failed to unapprove invoice', 'error')
    }
  }

  const handleGenerateInvoice = async (e) => {
    e.preventDefault()
    setGenerating(true)

    try {
      const res = await clientAdminApi.generateInvoice({
        client_id: parseInt(id),
        period_start: generatePeriodStart,
        period_end: generatePeriodEnd,
        exchange_rate: generateExchangeRate
      })

      if (res.data?.success) {
        showToast(`Invoice ${res.data.invoice_no} generated`)
        loadData()
        setShowModal(false)
      } else {
        showToast(res.data?.error || 'Failed to generate invoice', 'error')
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to generate invoice', 'error')
    }
    setGenerating(false)
  }

  const loadGeneratePreview = async () => {
    if (!generatePeriodStart || !generatePeriodEnd) return
    setLoadingPreview(true)
    try {
      const res = await clientAdminApi.getBotTimeline(id, generatePeriodStart, generatePeriodEnd)
      if (res.data?.success) {
        setGeneratePreview(res.data.timeline)
      }
    } catch (error) {
      console.error('Failed to load preview:', error)
    }
    setLoadingPreview(false)
  }

  const handleEditInvoice = async (e) => {
    e.preventDefault()
    if (!currentInvoice) return

    try {
      // First update the basic invoice details
      const basicData = {
        issue_date: editInvoiceData.issue_date,
        due_date: editInvoiceData.due_date,
        notes: editInvoiceData.notes,
        exchange_rate: editInvoiceData.exchange_rate
      }
      await clientAdminApi.updateInvoice(currentInvoice.id, basicData)

      // Now sync line items - delete all existing and add new ones
      // First, delete all existing line items
      const existingCount = (currentInvoice.line_items || []).length
      for (let i = existingCount - 1; i >= 0; i--) {
        await clientAdminApi.deleteLineItem(currentInvoice.id, i)
      }

      // Then add all line items from editInvoiceData
      for (const item of (editInvoiceData.line_items || [])) {
        await clientAdminApi.addLineItem(currentInvoice.id, {
          description: item.description,
          campaign: item.dialer_name,
          qty: item.bot_count,
          rate: item.price_per_bot,
          days: item.days,
          date_from: item.date_from,
          date_to: item.date_to,
          amount_usd: item.amount_usd
        })
      }

      showToast('Invoice updated')
      loadData()
    } catch (error) {
      console.error('Error updating invoice:', error)
      showToast('Failed to update invoice', 'error')
    }
    setShowModal(false)
  }

  const handleAddLineItem = async (e) => {
    e.preventDefault()
    if (!currentInvoice) return

    try {
      const res = await clientAdminApi.addLineItem(currentInvoice.id, lineItemData)
      if (res.data?.success) {
        showToast('Line item added')
        loadData()
      } else {
        showToast(res.data?.error || 'Failed to add line item', 'error')
      }
    } catch (error) {
      showToast('Failed to add line item', 'error')
    }
    setShowModal(false)
  }

  const handleDeleteLineItem = async (invoiceId, index) => {
    if (!window.confirm('Delete this line item?')) return

    try {
      const res = await clientAdminApi.deleteLineItem(invoiceId, index)
      if (res.data?.success) {
        showToast('Line item deleted')
        loadData()
      } else {
        showToast(res.data?.error || 'Failed to delete line item', 'error')
      }
    } catch (error) {
      showToast('Failed to delete line item', 'error')
    }
  }

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_no}? This cannot be undone.`)) return

    try {
      const res = await clientAdminApi.deleteInvoice(invoice.id)
      if (res.data?.success) {
        showToast('Invoice deleted')
        loadData()
      } else {
        showToast(res.data?.error || 'Failed to delete invoice', 'error')
      }
    } catch (error) {
      showToast('Failed to delete invoice', 'error')
    }
  }

  const downloadInvoicePdf = async (invoice) => {
    setDownloading(invoice.id)
    try {
      const res = await clientAdminApi.getInvoicePdf(invoice.id)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${invoice.invoice_no}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download PDF:', error)
      showToast('Failed to download PDF', 'error')
    }
    setDownloading(null)
  }

  // Bot Changes handlers
  const handleAddBotChange = async (e) => {
    e.preventDefault()
    if (!botChangeForm.dialer_id || !botChangeForm.bot_count) {
      showToast('Dialer and bot count are required', 'error')
      return
    }

    try {
      const user = getCurrentUser()
      const res = await clientAdminApi.addBotChange(id, {
        dialer_id: parseInt(botChangeForm.dialer_id),
        change_type: botChangeType,
        bot_count: parseInt(botChangeForm.bot_count),
        effective_date: botChangeForm.effective_date,
        notes: botChangeForm.notes,
        _user_id: user.id,
        _user_name: user.username
      })

      if (res.data?.success) {
        showToast(`Bot ${botChangeType} recorded`)
        setBotChangeForm({ dialer_id: botChangeForm.dialer_id, bot_count: '', effective_date: new Date().toISOString().split('T')[0], notes: '' })
        loadData(true)
      } else {
        showToast(res.data?.error || 'Failed to add bot change', 'error')
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to add bot change', 'error')
    }
  }

  const handleEditBotChange = async (change) => {
    setEditingBotChange(change)
    setBotChangeType(change.change_type)
    setBotChangeForm({
      dialer_id: change.dialer_id,
      bot_count: change.bot_count,
      effective_date: change.effective_date?.split('T')[0] || '',
      notes: change.notes || ''
    })
  }

  const handleUpdateBotChange = async (e) => {
    e.preventDefault()
    if (!editingBotChange) return

    try {
      const res = await clientAdminApi.updateBotChange(id, editingBotChange.id, {
        change_type: botChangeType,
        bot_count: parseInt(botChangeForm.bot_count),
        effective_date: botChangeForm.effective_date,
        notes: botChangeForm.notes
      })

      if (res.data?.success) {
        showToast('Bot change updated')
        setEditingBotChange(null)
        setBotChangeForm({ dialer_id: '', bot_count: '', effective_date: new Date().toISOString().split('T')[0], notes: '' })
        setBotChangeType('addition')
        loadData(true)
      } else {
        showToast(res.data?.error || 'Failed to update', 'error')
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to update', 'error')
    }
  }

  const handleDeleteBotChange = async (changeId) => {
    if (!window.confirm('Delete this bot change?')) return

    try {
      const res = await clientAdminApi.deleteBotChange(id, changeId)
      if (res.data?.success) {
        showToast('Bot change deleted')
        loadData(true)
      } else {
        showToast(res.data?.error || 'Failed to delete', 'error')
      }
    } catch (error) {
      showToast('Failed to delete bot change', 'error')
    }
  }

  const cancelEditBotChange = () => {
    setEditingBotChange(null)
    setBotChangeForm({ dialer_id: '', bot_count: '', effective_date: new Date().toISOString().split('T')[0], notes: '' })
    setBotChangeType('addition')
  }

  // Compute running totals per dialer for bot changes display
  const getRunningTotal = (changes, currentChange) => {
    // Group by dialer and compute running total up to and including the current change
    const dialerId = currentChange.dialer_id
    let total = 0
    for (const c of changes) {
      if (c.dialer_id !== dialerId) continue
      if (c.change_type === 'initial' || c.change_type === 'addition') {
        total += c.bot_count
      } else {
        total -= c.bot_count
      }
      if (c.id === currentChange.id) break
    }
    return Math.max(0, total)
  }

  // Get filtered and sorted bot changes (sorted by date ASC for running totals)
  const sortedBotChanges = [...botChanges].sort((a, b) => {
    const dateA = new Date(a.effective_date)
    const dateB = new Date(b.effective_date)
    return dateA - dateB || new Date(a.created_at) - new Date(b.created_at)
  })

  const filteredBotChanges = botChangeFilterDialer === 'all'
    ? sortedBotChanges
    : sortedBotChanges.filter(c => c.dialer_id === parseInt(botChangeFilterDialer))

  // Calculate totals
  const totalBots = dialers.reduce((sum, d) => sum + (d.bot_count || 0), 0)
  const monthlyRevenue = dialers.reduce((sum, d) => {
    const price = d.custom_bot_price || client?.default_bot_price || 15
    return sum + (d.bot_count || 0) * price
  }, 0)
  const totalRevenueAllTime = invoices.reduce((sum, inv) => sum + parseFloat(inv.subtotal_usd || 0), 0)

  if (loading) {
    return (
      <div className="client-details-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading client...</p>
        </div>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="client-details-page">
        <div className="error-container">
          <AlertCircle size={48} />
          <p>Client not found</p>
          <button className="btn btn-primary" onClick={() => navigate('/clients')}>
            <ArrowLeft size={18} /> Back to Clients
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="client-details-page">
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className={`toast ${toast.type}`}
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
          >
            {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="details-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/clients')}>
            <ArrowLeft size={18} />
          </button>
          <div className="header-info">
            <div className="client-avatar-lg">
              {client.name?.charAt(0).toUpperCase()}
            </div>
            <div className="header-text">
              <h1>
                {client.name}
                <span className={`status-badge ${getStatusClass(client.status)}`}>
                  {client.status}
                </span>
                {!!client.grace_period_active && (
                  <span className="grace-badge">
                    <AlertTriangle size={10} /> Grace
                  </span>
                )}
              </h1>
              <div className="header-meta">
                {client.company && <span className="company">{client.company}</span>}
                {client.email && <span>{client.email}</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button
            onClick={() => loadData(true)}
            className="btn btn-secondary btn-icon"
            disabled={refreshing}
            title="Refresh"
          >
            <RefreshCw size={16} className={refreshing ? 'spinner' : ''} />
          </button>
          <button className="btn btn-secondary" onClick={() => openModal('edit')}>
            <Edit size={16} /> Edit
          </button>
          <button className="btn btn-primary" onClick={() => openModal('credit')}>
            <DollarSign size={16} /> Add Credit
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card balance" style={{ cursor: 'pointer' }} onClick={() => openModal('setBalance')} title="Click to set balance">
          <div className="stat-card-icon">
            <DollarSign size={20} />
          </div>
          <div className="stat-card-content">
            <span className={`stat-card-value ${getBalanceClass(client.balance)}`}>
              {formatCurrency(client.balance)}
            </span>
            <span className="stat-card-label">Account Balance <Edit size={12} style={{ opacity: 0.5, marginLeft: 4 }} /></span>
          </div>
        </div>
        <div className="stat-card bots">
          <div className="stat-card-icon">
            <Bot size={20} />
          </div>
          <div className="stat-card-content">
            <span className="stat-card-value">{totalBots} <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted)' }}>bots</span> / {dialers.length} <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted)' }}>dialers</span></span>
            <span className="stat-card-label">Bots & Dialers</span>
          </div>
        </div>
        <div className="stat-card revenue">
          <div className="stat-card-icon">
            <TrendingUp size={20} />
          </div>
          <div className="stat-card-content">
            <span className="stat-card-value">{formatCurrency(monthlyRevenue)}</span>
            <span className="stat-card-label">Monthly Revenue</span>
          </div>
        </div>
        <div className="stat-card total-rev">
          <div className="stat-card-icon">
            <Receipt size={20} />
          </div>
          <div className="stat-card-content">
            <span className="stat-card-value">{formatCurrency(totalRevenueAllTime)}</span>
            <span className="stat-card-label">Total Revenue</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <User size={16} /> Overview
          </button>
          <button
            className={`tab ${activeTab === 'dialers' ? 'active' : ''}`}
            onClick={() => setActiveTab('dialers')}
          >
            <Server size={16} /> Dialers ({dialers.length})
          </button>
          <button
            className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
            onClick={() => setActiveTab('billing')}
          >
            <History size={16} /> Billing History
          </button>
          <button
            className={`tab ${activeTab === 'bot-changes' ? 'active' : ''}`}
            onClick={() => setActiveTab('bot-changes')}
          >
            <GitBranch size={16} /> Bot Changes
          </button>
          <button
            className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            <Receipt size={16} /> Invoices ({invoices.length})
          </button>
          <button
            className={`tab ${activeTab === 'avatars' ? 'active' : ''}`}
            onClick={() => { setActiveTab('avatars'); loadClientAvatars() }}
          >
            <Users size={16} /> Avatars
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (() => {
          const latestInvoice = invoices.length > 0 ? invoices[0] : null
          const totalPaid = invoices.filter(i => i.status === 'paid').length
          const totalOverdue = invoices.filter(i => i.status === 'pending' && new Date(i.due_date) < new Date()).length
          const recentChanges = botChanges ? botChanges.slice(0, 10) : []
          const addedCount = recentChanges.filter(c => c.type === 'add' || c.type === 'initial').reduce((s, c) => s + (c.bot_count || 0), 0)
          const removedCount = recentChanges.filter(c => c.type === 'remove').reduce((s, c) => s + (c.bot_count || 0), 0)
          const waLink = (client.whatsapp_number || client.phone) ? `https://wa.me/${(client.whatsapp_number || client.phone || '').replace(/[^0-9]/g, '')}` : null

          return (
          <div className="overview-grid-v2">
            {/* Row 1: Contact + Billing + Quick Info */}

            {/* Contact Info Card */}
            <div className="info-card">
              <div className="info-card-header">
                <User size={16} />
                <h3>Contact Information</h3>
                <button className="card-edit-btn" onClick={() => openModal('edit')} title="Edit"><Edit size={14} /></button>
              </div>
              <div className="info-card-body">
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-item-left"><User size={14} /> Contact Person</span>
                    <span className="info-value">{client.contact_person || client.email || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-left"><Phone size={14} /> Phone</span>
                    <span className="info-value">{client.phone || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-left"><MessageCircle size={14} /> WhatsApp</span>
                    <span className="info-value">
                      {client.whatsapp_number ? (
                        <a href={waLink} target="_blank" rel="noopener noreferrer" className="wa-inline-link">
                          {client.whatsapp_number} <ExternalLink size={11} />
                        </a>
                      ) : '-'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-left"><Building2 size={14} /> Company</span>
                    <span className="info-value">{client.company || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-left"><User size={14} /> Username</span>
                    <span className="info-value">{client.username}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-left"><Calendar size={14} /> Member Since</span>
                    <span className="info-value">{formatDate(client.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Settings Card */}
            <div className="info-card">
              <div className="info-card-header">
                <CreditCard size={16} />
                <h3>Billing Settings</h3>
                <button className="card-edit-btn" onClick={() => openModal('edit')} title="Edit"><Edit size={14} /></button>
              </div>
              <div className="info-card-body">
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-item-left"><DollarSign size={14} /> Bot Price</span>
                    <span className="info-value">{formatCurrency(client.default_bot_price)}/bot</span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-left"><Calendar size={14} /> Next Invoice</span>
                    <span className="info-value">
                      {nextCycle && nextCycle.success
                        ? (() => {
                            const fmt = (s) => { try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) } catch { return s } }
                            return nextCycle.source === 'last_invoice'
                              ? `${fmt(nextCycle.period_start)} → ${fmt(nextCycle.period_end)} (issue ${fmt(nextCycle.issue_date)})`
                              : `Initial: ${client.invoice_day || 1}${client.invoice_day === 1 ? 'st' : client.invoice_day === 2 ? 'nd' : client.invoice_day === 3 ? 'rd' : 'th'} of month`
                          })()
                        : `${client.invoice_day || 1}${client.invoice_day === 1 ? 'st' : client.invoice_day === 2 ? 'nd' : client.invoice_day === 3 ? 'rd' : 'th'} of month`
                      }
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-left"><Clock size={14} /> Payment Terms</span>
                    <span className="info-value">{client.payment_terms_days || 3} days</span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-left"><ShieldAlert size={14} /> Risk Status</span>
                    <span className={`info-value risk-val risk-${client.risk_status || 'low'}`}>{(client.risk_status || 'low').replace('_', ' ')}</span>
                  </div>
                  {client.follow_up_date && (
                    <div className="info-item">
                      <span className="info-item-left"><Calendar size={14} /> Follow-up</span>
                      <span className="info-value" style={{ color: new Date(client.follow_up_date) <= new Date() ? '#ef4444' : 'var(--text-primary)' }}>{formatDate(client.follow_up_date)}</span>
                    </div>
                  )}
                  {client.admin_extension_days > 0 && (
                    <div className="info-item">
                      <span className="info-item-left"><Clock size={14} /> Extension</span>
                      <span className="info-value">{client.admin_extension_days} days{client.admin_extension_until ? ` (until ${formatDate(client.admin_extension_until)})` : ''}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="info-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => openModal('extend')}>
                  <Clock size={14} /> Extend Access
                </button>
                {client.admin_extension_days > 0 && (
                  <button className="btn btn-secondary btn-sm" onClick={() => openModal('setExtension')}>
                    <Edit size={14} /> Edit Extension
                  </button>
                )}
              </div>
            </div>

            {/* Latest Invoice Card */}
            <div className="info-card">
              <div className="info-card-header">
                <Receipt size={16} />
                <h3>Latest Invoice</h3>
                <button className="card-edit-btn" onClick={() => setActiveTab('invoices')} title="View All"><Eye size={14} /></button>
              </div>
              <div className="info-card-body">
                {latestInvoice ? (
                  <>
                    <div className="latest-inv-summary">
                      <div className="latest-inv-amount">{formatCurrency(latestInvoice.subtotal_usd)}</div>
                      <span className={`invoice-status-mini ${latestInvoice.status === 'pending' && new Date(latestInvoice.due_date) < new Date() ? 'overdue' : latestInvoice.status}`}>
                        {latestInvoice.status === 'pending' && new Date(latestInvoice.due_date) < new Date() ? 'Overdue' : latestInvoice.status?.charAt(0).toUpperCase() + latestInvoice.status?.slice(1)}
                      </span>
                    </div>
                    <div className="info-list" style={{ marginTop: 12 }}>
                      <div className="info-item">
                        <span className="info-item-left"><FileText size={14} /> Invoice #</span>
                        <span className="info-value" style={{ fontFamily: "'SF Mono', Monaco, monospace", fontSize: '0.75rem' }}>{latestInvoice.invoice_no}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-item-left"><Calendar size={14} /> Period</span>
                        <span className="info-value" style={{ fontSize: '0.8rem' }}>{formatDate(latestInvoice.period_start)} - {formatDate(latestInvoice.period_end)}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-item-left"><Clock size={14} /> Due</span>
                        <span className="info-value">{formatDate(latestInvoice.due_date)}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-item-left"><Bot size={14} /> Bots</span>
                        <span className="info-value">{latestInvoice.total_bots} bots x {latestInvoice.total_days} days</span>
                      </div>
                    </div>
                    <div className="latest-inv-stats">
                      <span className="inv-mini-stat paid"><CheckCircle size={12} /> {totalPaid} paid</span>
                      <span className="inv-mini-stat pending"><Clock size={12} /> {invoices.filter(i => i.status === 'pending').length} pending</span>
                      {totalOverdue > 0 && <span className="inv-mini-stat overdue"><AlertTriangle size={12} /> {totalOverdue} overdue</span>}
                    </div>
                  </>
                ) : (
                  <div className="empty-card-state">
                    <Receipt size={28} />
                    <p>No invoices yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Bot Activity Card */}
            <div className="info-card">
              <div className="info-card-header">
                <BarChart3 size={16} />
                <h3>Bot Activity</h3>
                <button className="card-edit-btn" onClick={() => setActiveTab('bot-changes')} title="View All"><Eye size={14} /></button>
              </div>
              <div className="info-card-body">
                <div className="bot-activity-summary">
                  <div className="bot-act-stat added">
                    <TrendingUp size={16} />
                    <div>
                      <span className="bot-act-value">+{addedCount}</span>
                      <span className="bot-act-label">Added</span>
                    </div>
                  </div>
                  <div className="bot-act-stat removed">
                    <TrendingDown size={16} />
                    <div>
                      <span className="bot-act-value">-{removedCount}</span>
                      <span className="bot-act-label">Removed</span>
                    </div>
                  </div>
                  <div className="bot-act-stat net">
                    <Target size={16} />
                    <div>
                      <span className="bot-act-value">{totalBots}</span>
                      <span className="bot-act-label">Current</span>
                    </div>
                  </div>
                </div>

                {/* Mini timeline of recent changes */}
                {recentChanges.length > 0 ? (
                  <div className="bot-timeline">
                    {recentChanges.slice(0, 5).map((change, idx) => (
                      <div key={idx} className={`timeline-item ${change.type}`}>
                        <div className={`timeline-dot ${change.type}`}></div>
                        <div className="timeline-content">
                          <span className="timeline-action">
                            {change.type === 'add' ? '+' : change.type === 'remove' ? '-' : ''}{change.bot_count} bots
                            {change.dialer_name && <span className="timeline-dialer"> on {change.dialer_name}</span>}
                          </span>
                          <span className="timeline-date">{formatDate(change.effective_date || change.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-card-state" style={{ padding: '20px 0' }}>
                    <BarChart3 size={24} />
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes Card - Full Width */}
            <div className="info-card full-width">
              <div className="info-card-header">
                <FileText size={16} />
                <h3>Notes</h3>
                <button className="card-edit-btn" onClick={() => openModal('edit')} title="Edit"><Edit size={14} /></button>
              </div>
              <div className="info-card-body">
                {client.notes ? (
                  <p className="notes-text">{client.notes}</p>
                ) : (
                  <p className="notes-text" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No notes added yet</p>
                )}
              </div>
            </div>
          </div>
          )
        })()}

        {activeTab === 'dialers' && (
          <div className="dialers-section">
            <div className="section-header">
              <h3>Assigned Dialers</h3>
              <button className="btn btn-primary btn-sm" onClick={() => openModal('assign')}>
                <Plus size={16} /> Assign Dialer
              </button>
            </div>

            {dialers.length === 0 ? (
              <div className="empty-state">
                <Server size={48} />
                <p>No dialers assigned</p>
                <button className="btn btn-primary" onClick={() => openModal('assign')}>
                  <Plus size={18} /> Assign First Dialer
                </button>
              </div>
            ) : (
              <div className="dialers-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Dialer</th>
                      <th>Campaign</th>
                      <th>Host</th>
                      <th>Bots</th>
                      <th>Bot Price</th>
                      <th>Monthly</th>
                      <th>Assigned</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dialers.map(dialer => {
                      const price = dialer.custom_bot_price || client.default_bot_price || 15
                      const monthly = (dialer.bot_count || 0) * price
                      return (
                        <tr key={dialer.dialer_id}>
                          <td>
                            <Link to={`/dialer-details/${dialer.dialer_id}`} className="dialer-link">
                              {dialer.name}
                              <ChevronRight size={14} />
                            </Link>
                          </td>
                          <td>
                            <select
                              value={dialer.campaign_type || ''}
                              onChange={(e) => handleUpdateCampaignType(dialer.dialer_id, e.target.value)}
                              className="inline-select"
                            >
                              <option value="">—</option>
                              <option value="Medicare">Medicare</option>
                              <option value="Final Expense">Final Expense</option>
                              <option value="Home Improvement">Home Improvement</option>
                              <option value="Medicaid">Medicaid</option>
                              <option value="Real Estate">Real Estate</option>
                              <option value="Diabetes">Diabetes</option>
                              <option value="Auto">Auto</option>
                              <option value="MVA">MVA</option>
                              <option value="Med Alert">Med Alert</option>
                              <option value="ACA">ACA</option>
                              <option value="Solar">Solar</option>
                              <option value="MedBots">MedBots</option>
                              <option value="DNC">DNC</option>
                            </select>
                          </td>
                          <td><span className="host-badge">{dialer.host || '-'}</span></td>
                          <td><span className="bot-count">{dialer.bot_count || 0}</span></td>
                          <td>
                            {dialer.custom_bot_price ? (
                              <span className="custom-price">{formatCurrency(dialer.custom_bot_price)}</span>
                            ) : (
                              <span className="default-price">{formatCurrency(client.default_bot_price)}</span>
                            )}
                          </td>
                          <td><span className="monthly-revenue">{formatCurrency(monthly)}</span></td>
                          <td><span className="date">{formatDate(dialer.assigned_at)}</span></td>
                          <td>
                            <button
                              className="btn btn-icon btn-ghost danger"
                              onClick={() => handleRemoveDialer(dialer.dialer_id)}
                              title="Remove dialer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="billing-section">
            <div className="section-header">
              <h3>Transaction History</h3>
              <button className="btn btn-primary btn-sm" onClick={() => openModal('credit')}>
                <Plus size={16} /> Add Credit
              </button>
            </div>

            {transactions.length === 0 ? (
              <div className="empty-state">
                <History size={48} />
                <p>No transactions yet</p>
              </div>
            ) : (
              <div className="transactions-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Balance After</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => (
                      <tr key={tx.id}>
                        <td><span className="date">{formatDateTime(tx.created_at)}</span></td>
                        <td>
                          <span className={`tx-type ${tx.type}`}>
                            {getTransactionIcon(tx.type)}
                            {tx.type}
                          </span>
                        </td>
                        <td>
                          <span className="tx-description">{tx.description || '-'}</span>
                          {tx.bot_count > 0 && (
                            <span className="tx-details">
                              {tx.bot_count} bots @ {formatCurrency(tx.per_bot_rate)}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`tx-amount ${tx.type === 'debit' ? 'negative' : 'positive'}`}>
                            {tx.type === 'debit' ? '-' : '+'}{formatCurrency(Math.abs(tx.amount))}
                          </span>
                        </td>
                        <td>
                          <span className="tx-balance">{formatCurrency(tx.balance_after)}</span>
                        </td>
                        <td>
                          <span className="tx-user" title={tx.created_by_name || 'System'}>
                            {tx.created_by_name || <span className="text-muted">System</span>}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bot-changes' && (
          <div className="bot-changes-section">
            {/* Stats */}
            {botChangesStats && (
              <div className="stat-blocks">
                <div className="stat-block">
                  <div className="stat-icon"><Bot size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{botChangesStats.total_active_bots}</span>
                    <span className="stat-label">Total Active Bots</span>
                  </div>
                </div>
                <div className="stat-block">
                  <div className="stat-icon"><TrendingUp size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value" style={{ color: 'var(--success)' }}>+{botChangesStats.this_week?.added || 0}</span>
                    <span className="stat-label">Added This Week</span>
                  </div>
                </div>
                <div className="stat-block">
                  <div className="stat-icon"><TrendingDown size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value" style={{ color: 'var(--danger)' }}>-{botChangesStats.this_week?.removed || 0}</span>
                    <span className="stat-label">Removed This Week</span>
                  </div>
                </div>
                <div className="stat-block">
                  <div className="stat-icon"><TrendingUp size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value" style={{ color: botChangesStats.this_month?.net >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {botChangesStats.this_month?.net >= 0 ? '+' : ''}{botChangesStats.this_month?.net || 0}
                    </span>
                    <span className="stat-label">Net This Month</span>
                  </div>
                </div>
              </div>
            )}

            {/* Per-Dialer Summary */}
            {botChangesStats?.per_dialer?.length > 0 && (
              <div className="bot-dialer-summary">
                {botChangesStats.per_dialer.map(d => (
                  <div key={d.dialer_id} className="bot-dialer-chip">
                    <Server size={14} />
                    <span className="chip-name">{d.dialer_name}</span>
                    <span className="chip-count">{d.current_count} bots</span>
                  </div>
                ))}
              </div>
            )}

            {/* Add Change Form */}
            <div className="bot-change-form-card">
              <form onSubmit={editingBotChange ? handleUpdateBotChange : handleAddBotChange}>
                <div className="bot-change-form-row">
                  <div className="bot-change-field">
                    <label>Dialer</label>
                    <select
                      value={botChangeForm.dialer_id}
                      onChange={(e) => setBotChangeForm(prev => ({ ...prev, dialer_id: e.target.value }))}
                      required
                    >
                      <option value="">Select dialer...</option>
                      {dialers.map(d => (
                        <option key={d.dialer_id} value={d.dialer_id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bot-change-field">
                    <label>Type</label>
                    <div className="bot-change-type-toggle">
                      <button
                        type="button"
                        className={`type-btn initial ${botChangeType === 'initial' ? 'active' : ''}`}
                        onClick={() => setBotChangeType('initial')}
                      >
                        Initial
                      </button>
                      <button
                        type="button"
                        className={`type-btn add ${botChangeType === 'addition' ? 'active' : ''}`}
                        onClick={() => setBotChangeType('addition')}
                      >
                        <Plus size={14} /> Add
                      </button>
                      <button
                        type="button"
                        className={`type-btn remove ${botChangeType === 'removal' ? 'active' : ''}`}
                        onClick={() => setBotChangeType('removal')}
                      >
                        <Minus size={14} /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="bot-change-field">
                    <label>Bots</label>
                    <input
                      type="number"
                      min="1"
                      value={botChangeForm.bot_count}
                      onChange={(e) => setBotChangeForm(prev => ({ ...prev, bot_count: e.target.value }))}
                      placeholder="50"
                      required
                    />
                  </div>
                  <div className="bot-change-field">
                    <label>Effective Date</label>
                    <input
                      type="date"
                      value={botChangeForm.effective_date}
                      onChange={(e) => setBotChangeForm(prev => ({ ...prev, effective_date: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="bot-change-field bot-change-notes">
                    <label>Notes</label>
                    <input
                      type="text"
                      value={botChangeForm.notes}
                      onChange={(e) => setBotChangeForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Optional notes..."
                    />
                  </div>
                  <div className="bot-change-field bot-change-actions">
                    {editingBotChange ? (
                      <>
                        <button type="submit" className="btn btn-primary btn-sm">
                          <CheckCircle size={14} /> Update
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={cancelEditBotChange}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button type="submit" className="btn btn-primary btn-sm">
                        <Plus size={14} /> Add Change
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Filter */}
            <div className="bot-changes-toolbar">
              <div className="bot-changes-filter">
                <label>Filter by dialer:</label>
                <select
                  value={botChangeFilterDialer}
                  onChange={(e) => setBotChangeFilterDialer(e.target.value)}
                >
                  <option value="all">All Dialers</option>
                  {dialers.map(d => (
                    <option key={d.dialer_id} value={d.dialer_id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <span className="bot-changes-count">{filteredBotChanges.length} changes</span>
            </div>

            {/* Changes Table */}
            {filteredBotChanges.length === 0 ? (
              <div className="empty-state">
                <GitBranch size={48} />
                <p>No bot changes recorded yet</p>
                <p className="text-muted">Use the form above to log bot additions, removals, or set initial counts.</p>
              </div>
            ) : (
              <div className="bot-changes-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Dialer</th>
                      <th style={{ textAlign: 'center' }}>Type</th>
                      <th style={{ textAlign: 'center' }}>Bots</th>
                      <th style={{ textAlign: 'center' }}>Running Total</th>
                      <th>Notes</th>
                      <th>By</th>
                      <th style={{ width: '80px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...filteredBotChanges].reverse().map(change => (
                      <tr key={change.id} className={editingBotChange?.id === change.id ? 'editing-row' : ''}>
                        <td>{formatDate(change.effective_date)}</td>
                        <td>
                          <span className="campaign-badge">{change.dialer_name}</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`bot-change-type-badge ${change.change_type}`}>
                            {change.change_type === 'initial' ? 'Initial' :
                             change.change_type === 'addition' ? 'Added' : 'Removed'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`bot-change-count ${change.change_type === 'removal' ? 'negative' : 'positive'}`}>
                            {change.change_type === 'removal' ? '-' : '+'}
                            {change.bot_count}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="running-total">{getRunningTotal(sortedBotChanges, change)}</span>
                        </td>
                        <td>
                          <span className="bot-change-notes-text">{change.notes || '-'}</span>
                        </td>
                        <td>
                          <span className="tx-user">{change.created_by_name || <span className="text-muted">System</span>}</span>
                        </td>
                        <td>
                          <div className="bot-change-row-actions">
                            <button
                              className="btn btn-icon btn-ghost"
                              onClick={() => handleEditBotChange(change)}
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="btn btn-icon btn-ghost danger"
                              onClick={() => handleDeleteBotChange(change.id)}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="invoices-section">
            {/* Invoice Settings - Modern Card */}
            <div className="invoice-settings-modern">
              <div className="inv-settings-icon">
                <Calendar size={24} />
              </div>
              <div className="inv-settings-content">
                <h4>Invoice Settings</h4>
                <div className="inv-settings-grid">
                  <div className="inv-setting-block">
                    <span className="inv-setting-label">Client ID</span>
                    <span className="inv-setting-value inv-client-id">{client?.client_id || `C${String(client?.id).padStart(3, '0')}`}</span>
                  </div>
                  <div className="inv-setting-block">
                    <span className="inv-setting-label">Invoice Day</span>
                    <span className="inv-setting-value">
                      {client?.invoice_day || 1}<sup>{client?.invoice_day === 1 ? 'st' : client?.invoice_day === 2 ? 'nd' : client?.invoice_day === 3 ? 'rd' : 'th'}</sup> of each month
                    </span>
                  </div>
                  <div className="inv-setting-block">
                    <span className="inv-setting-label">Due Date</span>
                    <span className="inv-setting-value">Invoice day + 3 days</span>
                  </div>
                  <div className="inv-setting-block">
                    <span className="inv-setting-label">Default Rate</span>
                    <span className="inv-setting-value">${parseFloat(client?.default_bot_price || 11).toFixed(2)}/bot</span>
                  </div>
                </div>
              </div>
              <button className="inv-settings-edit-btn" onClick={() => openModal('edit')}>
                <Edit size={16} />
              </button>
            </div>

            {/* Invoice List */}
            <div className="section-header">
              <h3>Invoice History</h3>
              <button className="btn btn-primary btn-sm" onClick={() => openInvoiceModal('generateInvoice')}>
                <Receipt size={16} /> Generate Invoice
              </button>
            </div>

            {invoices.length === 0 ? (
              <div className="empty-state">
                <Receipt size={48} />
                <p>No invoices yet</p>
                <button className="btn btn-primary" onClick={() => openInvoiceModal('generateInvoice')}>
                  <Receipt size={18} /> Generate First Invoice
                </button>
              </div>
            ) : (
              <div className="invoices-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '30px' }}></th>
                      <th>Invoice #</th>
                      <th>Issue Date</th>
                      <th>Due Date</th>
                      <th>Period</th>
                      <th style={{ textAlign: 'center' }}>Bots</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(invoice => (
                      <React.Fragment key={invoice.id}>
                        <tr className={expandedInvoice === invoice.id ? 'expanded-row' : ''}>
                          <td>
                            <button
                              className="expand-btn"
                              onClick={() => setExpandedInvoice(expandedInvoice === invoice.id ? null : invoice.id)}
                            >
                              {expandedInvoice === invoice.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          </td>
                          <td>
                            <span className="invoice-no">{invoice.invoice_no}</span>
                          </td>
                          <td>{formatDate(invoice.issue_date)}</td>
                          <td>{formatDate(invoice.due_date)}</td>
                          <td>
                            <span className="period-text">{formatPeriod(invoice.period_start, invoice.period_end)}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{invoice.total_bots}</td>
                          <td style={{ textAlign: 'right' }}>
                            <span className="amount-text">{formatCurrency(invoice.subtotal_usd)}</span>
                            {invoice.status === 'partial' && invoice.paid_amount != null && (
                              <div className="partial-info">
                                <span className="paid-amount">Paid: {formatCurrency(invoice.paid_amount)}</span>
                                <span className="pending-amount">Pending: {formatCurrency(parseFloat(invoice.subtotal_usd) - parseFloat(invoice.paid_amount))}</span>
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`invoice-status-badge ${getInvoiceStatusClass(invoice.status, invoice.due_date)}`}>
                              {formatInvoiceStatus(invoice.status, invoice.due_date)}
                            </span>
                          </td>
                          <td>
                            <div className="invoice-actions">
                              <button
                                className="inv-action-btn download"
                                onClick={() => downloadInvoicePdf(invoice)}
                                disabled={downloading === invoice.id}
                                title="Download PDF"
                              >
                                {downloading === invoice.id ? (
                                  <span className="btn-spinner-sm"></span>
                                ) : (
                                  <Download size={14} />
                                )}
                              </button>
                              {/* Approve/Unapprove button */}
                              {invoice.is_approved ? (
                                <button
                                  className="inv-action-btn approved"
                                  onClick={() => handleUnapproveInvoice(invoice)}
                                  title="Unapprove (hide from client)"
                                >
                                  <ShieldCheck size={14} />
                                </button>
                              ) : (
                                <button
                                  className="inv-action-btn approve"
                                  onClick={() => handleApproveInvoice(invoice)}
                                  title="Approve (show to client)"
                                >
                                  <ShieldX size={14} />
                                </button>
                              )}
                              {/* Mark Paid - only show if approved and not already paid */}
                              {invoice.status !== 'paid' && invoice.is_approved && (
                                <button
                                  className="inv-action-btn paid"
                                  onClick={() => openInvoiceModal('markPaid', invoice)}
                                  title="Mark Paid"
                                >
                                  <CheckCircle size={14} />
                                </button>
                              )}
                              <button
                                className="inv-action-btn edit"
                                onClick={() => {
                                  setEditingInvoice(invoice)
                                  setShowInvoiceEditor(true)
                                }}
                                title="Edit Invoice"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                className="inv-action-btn delete"
                                onClick={() => handleDeleteInvoice(invoice)}
                                title="Delete Invoice"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded Line Items */}
                        {expandedInvoice === invoice.id && (
                          <tr className="line-items-row">
                            <td colSpan="9">
                              <div className="line-items-container">
                                <div className="line-items-header">
                                  <h4>Line Items</h4>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => openInvoiceModal('addLineItem', invoice)}
                                  >
                                    <Plus size={14} /> Add Line Item
                                  </button>
                                </div>
                                <table className="line-items-table">
                                  <thead>
                                    <tr>
                                      <th>Description</th>
                                      <th>Campaign</th>
                                      <th style={{ textAlign: 'center' }}>QTY</th>
                                      <th style={{ textAlign: 'right' }}>Rate</th>
                                      <th style={{ textAlign: 'center' }}>Days</th>
                                      <th style={{ textAlign: 'right' }}>Amount</th>
                                      <th style={{ width: '40px' }}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(invoice.line_items || []).map((item, idx) => (
                                      <tr key={idx}>
                                        <td>
                                          <div className="line-item-desc">
                                            <span>{item.description || 'Bot Deployment Services'}</span>
                                            {item.date_from && item.date_to && (
                                              <span className="line-item-dates">
                                                {formatDate(item.date_from)} - {formatDate(item.date_to)}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        <td>
                                          <span className="campaign-badge">{item.dialer_name || '-'}</span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{item.bot_count || 0}</td>
                                        <td style={{ textAlign: 'right' }}>${parseFloat(item.price_per_bot || 0).toFixed(2)}</td>
                                        <td style={{ textAlign: 'center' }}>{item.days || 30}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                          ${parseFloat(item.amount_usd || 0).toFixed(2)}
                                        </td>
                                        <td>
                                          <button
                                            className="line-item-delete"
                                            onClick={() => handleDeleteLineItem(invoice.id, idx)}
                                            title="Delete"
                                          >
                                            <X size={14} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                    {(!invoice.line_items || invoice.line_items.length === 0) && (
                                      <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>
                                          No line items
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Avatars Tab */}
        {activeTab === 'avatars' && (
          <div>
            <div className="info-card" style={{ marginBottom: 20 }}>
              <div className="info-card-header">
                <Users size={18} />
                <h3>Assigned Avatars</h3>
              </div>
              <div className="info-card-body">
                {clientAvatarsList.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>No avatars assigned yet</p>
                ) : (
                  <table className="data-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th>Avatar Name</th>
                        <th>Label</th>
                        <th>Campaign</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientAvatarsList.map(av => {
                        // Extract campaign from avatar name (e.g. "Auto-Moonlight-Quality" → "Auto", "Medicare - Moonlight" → "Medicare")
                        const nameParts = av.avatar_name.split(/[-–\s]+/)
                        const campaigns = ['Medicare', 'FE', 'Auto', 'ACA', 'Diabetic', 'MVA', 'Solar', 'Mortgage']
                        const detectedCampaign = campaigns.find(c => av.avatar_name.toLowerCase().includes(c.toLowerCase())) || av.campaign || nameParts[0] || '-'
                        return (
                          <tr key={av.avatar_id}>
                            <td><strong>{av.avatar_name}</strong></td>
                            <td>
                              <input type="text" defaultValue={av.label || ''} placeholder="Add label..."
                                onBlur={async (e) => {
                                  const val = e.target.value
                                  if (val === (av.label || '')) return
                                  try {
                                    await clientAdminApi.assignClientAvatar(id, { avatar_id: av.avatar_id, label: val, avatar_type: av.avatar_type })
                                    showToast('Label updated')
                                    loadClientAvatars()
                                  } catch { showToast('Failed', 'error') }
                                }}
                                style={{ width: '100%', padding: '5px 8px', border: '1px solid transparent', borderRadius: 4, background: 'transparent', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                                onFocus={e => e.target.style.borderColor = 'var(--border-color)'}
                                onBlurCapture={e => e.target.style.borderColor = 'transparent'}
                              />
                            </td>
                            <td>{detectedCampaign}</td>
                            <td style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => {
                                const newLabel = prompt('Edit label:', av.label || '')
                                if (newLabel === null) return
                                clientAdminApi.assignClientAvatar(id, { avatar_id: av.avatar_id, label: newLabel, avatar_type: av.avatar_type })
                                  .then(() => { showToast('Updated'); loadClientAvatars() })
                                  .catch(() => showToast('Failed', 'error'))
                              }} style={{ color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }} title="Edit">
                                <Edit size={14} />
                              </button>
                              <button onClick={() => handleRemoveAvatar(av.avatar_id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }} title="Remove">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="info-card">
              <div className="info-card-header">
                <Plus size={18} />
                <h3>Assign Avatar</h3>
              </div>
              <div className="info-card-body">
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 12, alignItems: 'end' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Avatar</label>
                    <select value={selectedAvatarToAdd} onChange={e => setSelectedAvatarToAdd(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
                      <option value="">Select avatar...</option>
                      {allAvatars.filter(a => !a.is_archived).map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Label (optional)</label>
                    <input type="text" value={avatarLabel} onChange={e => setAvatarLabel(e.target.value)} placeholder="e.g. Quality Medicare"
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)' }} />
                  </div>
                  <button onClick={handleAssignAvatar} disabled={!selectedAvatarToAdd}
                    className="btn btn-primary" style={{ padding: '8px 16px' }}>
                    <Plus size={14} /> Assign
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>
                  {modalMode === 'edit' && 'Edit Client'}
                  {modalMode === 'credit' && 'Add Credit'}
                  {modalMode === 'setBalance' && 'Set Balance'}
                  {modalMode === 'extend' && 'Extend Access'}
                  {modalMode === 'setExtension' && 'Edit Extension'}
                  {modalMode === 'assign' && 'Assign Dialer'}
                  {modalMode === 'markPaid' && `Mark as Paid - ${currentInvoice?.invoice_no}`}
                  {modalMode === 'markPartial' && `Mark as Partial - ${currentInvoice?.invoice_no}`}
                  {modalMode === 'generateInvoice' && 'Generate Invoice'}
                  {modalMode === 'editInvoice' && `Edit Invoice - ${currentInvoice?.invoice_no}`}
                  {modalMode === 'addLineItem' && `Add Line Item - ${currentInvoice?.invoice_no}`}
                </h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  <X size={20} />
                </button>
              </div>

              {['edit', 'credit', 'setBalance', 'extend', 'setExtension', 'assign'].includes(modalMode) && (
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {modalMode === 'edit' && (
                    <div className="edit-client-form">
                      {/* Basic Info Section */}
                      <div className="edit-section">
                        <h4 className="edit-section-title">Basic Information</h4>
                        <div className="edit-grid edit-grid-3">
                          <div className="edit-field">
                            <label>Client ID</label>
                            <input
                              type="text"
                              value={formData.client_id}
                              onChange={(e) => setFormData({ ...formData, client_id: e.target.value.toUpperCase() })}
                              placeholder="e.g., 008"
                              className="client-id-input"
                            />
                          </div>
                          <div className="edit-field">
                            <label>Name *</label>
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="edit-field">
                            <label>Company</label>
                            <input
                              type="text"
                              value={formData.company}
                              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Contact Section */}
                      <div className="edit-section">
                        <h4 className="edit-section-title">Contact Details</h4>
                        <div className="edit-grid edit-grid-2">
                          <div className="edit-field">
                            <label>Email</label>
                            <input
                              type="email"
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                          </div>
                          <div className="edit-field">
                            <label>Phone</label>
                            <input
                              type="text"
                              value={formData.phone}
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                          </div>
                          <div className="edit-field">
                            <label>Contact Person</label>
                            <input
                              type="text"
                              value={formData.contact_person}
                              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                              placeholder="Primary contact name"
                            />
                          </div>
                          <div className="edit-field">
                            <label>WhatsApp Number</label>
                            <input
                              type="text"
                              value={formData.whatsapp_number}
                              onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                              placeholder="+92 300 1234567"
                            />
                          </div>
                          <div className="edit-field">
                            <label>Follow-up Date</label>
                            <input
                              type="date"
                              value={formData.follow_up_date}
                              onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                            />
                          </div>
                          <div className="edit-field">
                            <label>Payment Terms (days)</label>
                            <input
                              type="number"
                              value={formData.payment_terms_days}
                              onChange={(e) => setFormData({ ...formData, payment_terms_days: parseInt(e.target.value) || 7 })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Billing Section */}
                      <div className="edit-section">
                        <h4 className="edit-section-title">Billing Settings</h4>
                        <div className="edit-grid edit-grid-3">
                          <div className="edit-field">
                            <label>Default Bot Price</label>
                            <div className="input-with-prefix">
                              <span className="input-prefix">$</span>
                              <input
                                type="number"
                                step="0.01"
                                value={formData.default_bot_price}
                                onChange={(e) => setFormData({ ...formData, default_bot_price: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="edit-field">
                            <label>Invoice Day</label>
                            <select
                              value={formData.invoice_day}
                              onChange={(e) => setFormData({ ...formData, invoice_day: parseInt(e.target.value) })}
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                                <option key={day} value={day}>{day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of month</option>
                              ))}
                            </select>
                          </div>
                          <div className="edit-field">
                            <label>Status</label>
                            <select
                              value={formData.status}
                              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                              className={`status-select status-${formData.status}`}
                            >
                              <option value="active">Active</option>
                              <option value="pending">Pending</option>
                              <option value="suspended">Suspended</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Security Section */}
                      <div className="edit-section">
                        <h4 className="edit-section-title">Security</h4>
                        <div className="edit-grid edit-grid-1">
                          <div className="edit-field">
                            <label>New Password</label>
                            <input
                              type="password"
                              value={formData.password}
                              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                              placeholder="Leave blank to keep current"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Notes Section */}
                      <div className="edit-section">
                        <h4 className="edit-section-title">Notes</h4>
                        <div className="edit-field">
                          <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            rows={3}
                            placeholder="Internal notes about this client..."
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {modalMode === 'credit' && (
                    <>
                      <div className="current-balance">
                        <span>Current Balance:</span>
                        <strong className={getBalanceClass(client.balance)}>
                          {formatCurrency(client.balance)}
                        </strong>
                      </div>
                      <div className="form-group">
                        <label>Amount to Add *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={creditAmount}
                          onChange={(e) => setCreditAmount(e.target.value)}
                          required
                          placeholder="100.00"
                          autoFocus
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <input
                          type="text"
                          value={creditDescription}
                          onChange={(e) => setCreditDescription(e.target.value)}
                          placeholder="Payment received, etc."
                        />
                      </div>
                    </>
                  )}

                  {modalMode === 'setBalance' && (
                    <>
                      <div className="info-box">
                        <AlertTriangle size={18} />
                        <p>Manually set the client's balance to an exact amount. This will be logged as an adjustment.</p>
                      </div>
                      <div className="current-balance">
                        <span>Current Balance:</span>
                        <strong className={getBalanceClass(client.balance)}>
                          {formatCurrency(client.balance)}
                        </strong>
                      </div>
                      <div className="form-group">
                        <label>New Balance *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={setBalanceAmount}
                          onChange={(e) => setSetBalanceAmount(e.target.value)}
                          required
                          placeholder="0.00"
                          autoFocus
                        />
                      </div>
                      <div className="form-group">
                        <label>Reason</label>
                        <input
                          type="text"
                          value={setBalanceReason}
                          onChange={(e) => setSetBalanceReason(e.target.value)}
                          placeholder="Correction, refund adjustment, etc."
                        />
                      </div>
                    </>
                  )}

                  {modalMode === 'extend' && (
                    <>
                      <div className="info-box">
                        <AlertTriangle size={18} />
                        <p>Extend client access without requiring payment. Use for special arrangements.</p>
                      </div>
                      <div className="form-group">
                        <label>Days to Extend *</label>
                        <input
                          type="number"
                          value={extensionDays}
                          onChange={(e) => setExtensionDays(e.target.value)}
                          required
                          placeholder="7"
                          autoFocus
                        />
                      </div>
                      <div className="form-group">
                        <label>Reason</label>
                        <input
                          type="text"
                          value={extensionReason}
                          onChange={(e) => setExtensionReason(e.target.value)}
                          placeholder="Promotional offer, payment pending, etc."
                        />
                      </div>
                    </>
                  )}

                  {modalMode === 'setExtension' && (
                    <>
                      <div className="info-box">
                        <AlertTriangle size={18} />
                        <p>Set extension days to an exact number. Set to 0 to clear the extension entirely.</p>
                      </div>
                      <div className="current-balance">
                        <span>Current Extension:</span>
                        <strong>{client.admin_extension_days || 0} days</strong>
                      </div>
                      <div className="form-group">
                        <label>New Extension Days *</label>
                        <input
                          type="number"
                          min="0"
                          value={setExtensionDaysValue}
                          onChange={(e) => setSetExtensionDaysValue(e.target.value)}
                          required
                          placeholder="0"
                          autoFocus
                        />
                        <p className="form-hint">Set to 0 to remove extension</p>
                      </div>
                      <div className="form-group">
                        <label>Reason</label>
                        <input
                          type="text"
                          value={editExtensionReason}
                          onChange={(e) => setEditExtensionReason(e.target.value)}
                          placeholder="Correction, changed arrangement, etc."
                        />
                      </div>
                    </>
                  )}

                  {modalMode === 'assign' && (
                    <>
                      <div className="form-group">
                        <label>Select Dialer *</label>
                        <select
                          value={selectedDialer}
                          onChange={(e) => setSelectedDialer(e.target.value)}
                          required
                        >
                          <option value="">Choose a dialer...</option>
                          {availableDialers.map(dialer => (
                            <option key={dialer.id} value={dialer.id}>
                              {dialer.name} ({dialer.bot_count} bots)
                            </option>
                          ))}
                        </select>
                        {availableDialers.length === 0 && (
                          <p className="form-hint warning">No unassigned dialers available</p>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Custom Bot Price (optional)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={customBotPrice}
                          onChange={(e) => setCustomBotPrice(e.target.value)}
                          placeholder={`Default: ${formatCurrency(client.default_bot_price)}/bot`}
                        />
                        <p className="form-hint">Leave blank to use client's default rate</p>
                      </div>
                      <div className="form-group">
                        <label>Campaign Type (optional)</label>
                        <select
                          value={campaignType}
                          onChange={(e) => setCampaignType(e.target.value)}
                        >
                          <option value="">None</option>
                          <option value="Medicare">Medicare</option>
                          <option value="Final Expense">Final Expense</option>
                          <option value="Home Improvement">Home Improvement</option>
                          <option value="Medicaid">Medicaid</option>
                          <option value="Real Estate">Real Estate</option>
                          <option value="Diabetes">Diabetes</option>
                          <option value="Auto">Auto</option>
                          <option value="MVA">MVA</option>
                          <option value="Med Alert">Med Alert</option>
                          <option value="ACA">ACA</option>
                          <option value="Solar">Solar</option>
                          <option value="MedBots">MedBots</option>
                          <option value="DNC">DNC</option>
                        </select>
                        <p className="form-hint">Used in invoice Campaign column instead of dialer name</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Footer for non-invoice modals */}
                {['edit', 'credit', 'setBalance', 'extend', 'setExtension', 'assign'].includes(modalMode) && (
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {modalMode === 'edit' && 'Save Changes'}
                    {modalMode === 'credit' && 'Add Credit'}
                    {modalMode === 'setBalance' && 'Set Balance'}
                    {modalMode === 'extend' && 'Extend Access'}
                    {modalMode === 'setExtension' && 'Update Extension'}
                    {modalMode === 'assign' && 'Assign Dialer'}
                  </button>
                </div>
                )}
              </form>
              )}

              {/* Mark Paid / Partial Form */}
              {(modalMode === 'markPaid' || modalMode === 'markPartial') && (
                <form onSubmit={handleMarkPaid}>
                  <div className="modal-body">
                    <div className="current-balance" style={{ marginBottom: '20px' }}>
                      <span>Invoice Amount</span>
                      <strong>{formatCurrency(currentInvoice?.subtotal_usd || 0)}</strong>
                    </div>
                    <div className="form-group">
                      <label>Amount Paid (USD) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={paidAmount}
                        onChange={(e) => setPaidAmount(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Payment Reference</label>
                      <input
                        type="text"
                        value={paidReference}
                        onChange={(e) => setPaidReference(e.target.value)}
                        placeholder="Transaction ID, check number, etc."
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      <CheckCircle size={16} />
                      Mark as {modalMode === 'markPaid' ? 'Paid' : 'Partial'}
                    </button>
                  </div>
                </form>
              )}

              {/* Generate Invoice Form */}
              {modalMode === 'generateInvoice' && (
                <form onSubmit={handleGenerateInvoice}>
                  <div className="modal-body modal-body-large">
                    <div className="info-box">
                      <Calendar size={18} />
                      <p>Generate an invoice for <strong>{client?.name}</strong>.
                        {botChanges.length > 0 ? ' Pro-rata billing based on bot change timeline.' : ' Based on current dashboard bot counts.'}
                      </p>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Period Start</label>
                        <input
                          type="date"
                          value={generatePeriodStart}
                          onChange={(e) => setGeneratePeriodStart(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Period End</label>
                        <input
                          type="date"
                          value={generatePeriodEnd}
                          onChange={(e) => setGeneratePeriodEnd(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Exchange Rate</label>
                        <input
                          type="number"
                          step="0.01"
                          value={generateExchangeRate}
                          onChange={(e) => setGenerateExchangeRate(parseFloat(e.target.value) || 280)}
                        />
                      </div>
                    </div>

                    {botChanges.length > 0 && (
                      <div style={{ marginTop: 8, marginBottom: 8 }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={loadGeneratePreview} disabled={loadingPreview}>
                          <RefreshCw size={14} className={loadingPreview ? 'spinner' : ''} /> {loadingPreview ? 'Loading...' : 'Load Preview'}
                        </button>
                      </div>
                    )}

                    {/* Preview table */}
                    {generatePreview && generatePreview.length > 0 && (
                      <div className="generate-preview">
                        <h4 style={{ fontSize: 13, marginBottom: 8, color: 'var(--text-muted)' }}>Invoice Preview</h4>
                        <table className="data-table" style={{ fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th>Dialer</th>
                              <th>Period</th>
                              <th style={{ textAlign: 'center' }}>Bots</th>
                              <th style={{ textAlign: 'right' }}>Rate</th>
                              <th style={{ textAlign: 'center' }}>Days</th>
                              <th style={{ textAlign: 'right' }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {generatePreview.map((seg, idx) => (
                              <tr key={idx}>
                                <td><span className="campaign-badge">{seg.dialer_name}</span></td>
                                <td>{formatDate(seg.date_from)} - {formatDate(seg.date_to)}</td>
                                <td style={{ textAlign: 'center' }}>{seg.bot_count}</td>
                                <td style={{ textAlign: 'right' }}>${parseFloat(seg.rate).toFixed(2)}</td>
                                <td style={{ textAlign: 'center' }}>{seg.days}</td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>${parseFloat(seg.amount_usd).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan="5" style={{ textAlign: 'right', fontWeight: 600 }}>Total:</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>
                                ${generatePreview.reduce((sum, s) => sum + parseFloat(s.amount_usd || 0), 0).toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}

                    {generatePreview && generatePreview.length === 0 && (
                      <div className="info-box" style={{ marginTop: 8 }}>
                        <AlertTriangle size={18} />
                        <p>No billable segments found for this period. Check bot changes.</p>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={generating}>
                      {generating ? (
                        <><RefreshCw size={16} className="spinner" /> Generating...</>
                      ) : (
                        <><Receipt size={16} /> Generate Invoice</>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {/* Edit Invoice Form */}
              {modalMode === 'editInvoice' && (
                <form onSubmit={handleEditInvoice}>
                  <div className="modal-body modal-body-large">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Issue Date</label>
                        <input
                          type="date"
                          value={editInvoiceData.issue_date}
                          onChange={(e) => setEditInvoiceData(prev => ({ ...prev, issue_date: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Due Date</label>
                        <input
                          type="date"
                          value={editInvoiceData.due_date}
                          onChange={(e) => setEditInvoiceData(prev => ({ ...prev, due_date: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Exchange Rate (PKR)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editInvoiceData.exchange_rate}
                          onChange={(e) => setEditInvoiceData(prev => ({ ...prev, exchange_rate: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Line Items Section */}
                    <div className="edit-line-items-section">
                      <div className="section-header-inline">
                        <h4>Line Items</h4>
                        <div className="section-actions">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={addPreviousBalanceRow}>
                            <Plus size={14} /> Previous Balance
                          </button>
                        </div>
                      </div>

                      {/* Existing Line Items */}
                      <div className="edit-line-items-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th style={{ width: '100px' }}>Campaign</th>
                              <th style={{ width: '60px', textAlign: 'center' }}>Qty</th>
                              <th style={{ width: '80px', textAlign: 'right' }}>Rate</th>
                              <th style={{ width: '60px', textAlign: 'center' }}>Days</th>
                              <th style={{ width: '100px', textAlign: 'right' }}>Amount ($)</th>
                              <th style={{ width: '40px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(editInvoiceData.line_items || []).map((item, idx) => (
                              <tr key={idx}>
                                <td>
                                  <span className="line-desc">{item.description || 'Bot Deployment'}</span>
                                </td>
                                <td>
                                  <span className="campaign-badge-sm">{item.dialer_name || '-'}</span>
                                </td>
                                <td style={{ textAlign: 'center' }}>{item.bot_count || '-'}</td>
                                <td style={{ textAlign: 'right' }}>{item.price_per_bot ? `$${parseFloat(item.price_per_bot).toFixed(2)}` : '-'}</td>
                                <td style={{ textAlign: 'center' }}>{item.days || '-'}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    className="amount-input"
                                    value={item.amount_usd}
                                    onChange={(e) => updateLineItemAmount(idx, e.target.value)}
                                  />
                                </td>
                                <td>
                                  <button type="button" className="line-item-delete" onClick={() => removeLineItemFromEdit(idx)}>
                                    <X size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {(!editInvoiceData.line_items || editInvoiceData.line_items.length === 0) && (
                              <tr>
                                <td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>
                                  No line items - add one below
                                </td>
                              </tr>
                            )}
                          </tbody>
                          <tfoot>
                            <tr className="total-row">
                              <td colSpan="5" style={{ textAlign: 'right', fontWeight: 600 }}>Total:</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>
                                ${calculateEditTotal().toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Add New Line Item */}
                      <div className="add-line-item-form">
                        <h5>Add Line Item</h5>
                        <div className="add-line-grid">
                          <div className="form-group">
                            <label>Description</label>
                            <input
                              type="text"
                              value={lineItemData.description}
                              onChange={(e) => setLineItemData(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Bot Deployment Services"
                            />
                          </div>
                          <div className="form-group">
                            <label>Campaign</label>
                            <input
                              type="text"
                              value={lineItemData.campaign}
                              onChange={(e) => setLineItemData(prev => ({ ...prev, campaign: e.target.value }))}
                              placeholder="Campaign name"
                            />
                          </div>
                          <div className="form-group">
                            <label>Qty</label>
                            <input
                              type="number"
                              value={lineItemData.qty}
                              onChange={(e) => setLineItemData(prev => ({ ...prev, qty: parseInt(e.target.value) || 0 }))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Rate ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={lineItemData.rate}
                              onChange={(e) => setLineItemData(prev => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Days</label>
                            <input
                              type="number"
                              value={lineItemData.days}
                              onChange={(e) => setLineItemData(prev => ({ ...prev, days: parseInt(e.target.value) || 0 }))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Amount ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={lineItemData.amount_usd}
                              onChange={(e) => setLineItemData(prev => ({ ...prev, amount_usd: parseFloat(e.target.value) || 0 }))}
                              placeholder={`Calc: ${((lineItemData.qty || 0) * (lineItemData.rate || 0) * (lineItemData.days || 0) / 30).toFixed(2)}`}
                            />
                          </div>
                          <div className="form-group add-btn-group">
                            <button type="button" className="btn btn-primary btn-sm" onClick={addLineItemToEdit}>
                              <Plus size={14} /> Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Notes</label>
                      <textarea
                        value={editInvoiceData.notes}
                        onChange={(e) => setEditInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={2}
                        placeholder="Internal notes..."
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      <CheckCircle size={16} />
                      Update Invoice
                    </button>
                  </div>
                </form>
              )}

              {/* Add Line Item Form */}
              {modalMode === 'addLineItem' && (
                <form onSubmit={handleAddLineItem}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Description</label>
                      <input
                        type="text"
                        value={lineItemData.description}
                        onChange={(e) => setLineItemData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Bot Deployment Services"
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Campaign</label>
                        <input
                          type="text"
                          value={lineItemData.campaign}
                          onChange={(e) => setLineItemData(prev => ({ ...prev, campaign: e.target.value }))}
                          placeholder="Campaign name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Quantity (Bots)</label>
                        <input
                          type="number"
                          value={lineItemData.qty}
                          onChange={(e) => setLineItemData(prev => ({ ...prev, qty: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Rate ($/bot)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={lineItemData.rate}
                          onChange={(e) => setLineItemData(prev => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Days</label>
                        <input
                          type="number"
                          value={lineItemData.days}
                          onChange={(e) => setLineItemData(prev => ({ ...prev, days: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Date From</label>
                        <input
                          type="date"
                          value={lineItemData.date_from}
                          onChange={(e) => setLineItemData(prev => ({ ...prev, date_from: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Date To</label>
                        <input
                          type="date"
                          value={lineItemData.date_to}
                          onChange={(e) => setLineItemData(prev => ({ ...prev, date_to: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Amount (USD)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={lineItemData.amount_usd}
                        onChange={(e) => setLineItemData(prev => ({ ...prev, amount_usd: parseFloat(e.target.value) || 0 }))}
                      />
                      <p className="form-hint">
                        Calculated: ${((lineItemData.qty || 0) * (lineItemData.rate || 0) * (lineItemData.days || 0) / 30).toFixed(2)} (qty x rate x days / 30)
                      </p>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      <Plus size={16} />
                      Add Line Item
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invoice Editor Modal */}
      {showInvoiceEditor && editingInvoice && (
        <InvoiceEditor
          invoice={editingInvoice}
          onClose={() => {
            setShowInvoiceEditor(false)
            setEditingInvoice(null)
          }}
          onSave={() => {
            setShowInvoiceEditor(false)
            setEditingInvoice(null)
            loadData(true)
          }}
        />
      )}
    </div>
  )
}

export default ClientDetails
